import { Variant, Product, ProductPrices } from 'types/entity/product';

interface ShippingMethodLike {
  shippingMethodKey?: string;
}

export const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(n, max));

/**
 * Extract deduplicated P2R retailer prefixes (e.g. "p2r-cvs") from shipping
 * method keys (e.g. "p2r-cvs-store-pickup").
 */
export function getP2rRetailerPrefixes(shippingMethods?: ShippingMethodLike[]): string[] {
  if (!shippingMethods?.length) return [];
  const prefixes = shippingMethods
    .filter((m) => m.shippingMethodKey?.toLowerCase().startsWith('p2r'))
    .map((m) => {
      const parts = m.shippingMethodKey!.toLowerCase().split('-');
      return `${parts[0]}-${parts[1]}`;
    });
  return [...new Set(prefixes)];
}

/**
 * Filter a variant's channel prices, keeping all non-P2R prices and only
 * P2R prices whose retailer prefix matches an active shipping method.
 */
export function filterActivePrices(prices: ProductPrices[] | undefined, p2rRetailerPrefixes: string[]): ProductPrices[] {
  if (!prices) return [];
  return prices.filter((p) => {
    const channelKey = p?.channel?.obj?.key?.toLowerCase();
    if (!channelKey || !channelKey.startsWith('p2r-')) return true; // non-P2R → keep
    const parts = channelKey.split('-');
    const retailerPrefix = `${parts[0]}-${parts[1]}`; // e.g. "p2r-cvs"
    return p2rRetailerPrefixes.includes(retailerPrefix);
  });
}

export function getWindowStart(active: number, total: number, visible: number) {
  if (total <= visible) return 0;

  const centerPos = Math.floor(visible / 2);
  const start = active - centerPos;

  return clamp(start, 0, total - visible);
}
export function getMinimumPriceProduct(
  product: any,
  p2rRetailerPrefixes?: string[]
) {
  const variants = product?.variants;

  if (!Array.isArray(variants) || variants.length === 0) {
    return null;
  }

  let minVariant: any = null;
  let minAmount: number | null = null;

  for (const variant of variants) {
    const activePrices = p2rRetailerPrefixes
      ? filterActivePrices(variant?.prices, p2rRetailerPrefixes)
      : variant?.prices ?? [];

    const allPrices = [
      variant?.price?.centAmount,
      ...activePrices.map((p: any) => p?.value?.centAmount),
    ].filter(
      (amount): amount is number =>
        typeof amount === 'number' && Number.isFinite(amount)
    );

    const allDiscounts = [
      variant?.discountedPrice?.value?.centAmount,
      ...activePrices.map(
        (p: any) => p?.discounted?.value?.centAmount
      ),
    ].filter(
      (amount): amount is number =>
        typeof amount === 'number' && Number.isFinite(amount)
    );

    const regularAmount =
      allPrices.length > 0
        ? Math.min(...allPrices)
        : null;

    const discountedAmount =
      allDiscounts.length > 0
        ? Math.min(...allDiscounts)
        : null;

    const amount = discountedAmount ?? regularAmount;

    if (amount == null) {
      continue;
    }

    if (minAmount === null || amount < minAmount) {
      minAmount = amount;
      minVariant = variant;
    }
  }

  return minVariant;
}

export const getVariantIndex = (product: Product, minVariantId?: string, fallbackIndex = 0) => {
  const index = product?.variants?.findIndex((v) => v.id === minVariantId);
  return index >= 0 ? index : fallbackIndex;
};

export const getFirstAvailableVariant = (variants?: Variant[]) =>
  variants?.find(
    ({ isOnStock, attributes }) =>
      isOnStock &&
      attributes?.product_builder_universal &&
      attributes.product_builder_universal !== 'None' &&
      !attributes.mediaclip_product_id_universal,
  );

export const hasDuplicatePrice = (variants?: Variant[]) => {
  const seen = new Set<number>();
  return (
    variants?.some((v) => {
      const price = v?.price?.centAmount;
      if (typeof price !== 'number') return false;
      if (seen.has(price)) return true;
      seen.add(price);
      return false;
    }) ?? false
  );
};

export const resolveVariantIndex = (product: Product, minVariantId?: string, fallbackIndex = 0) => {
  const index = product?.variants?.findIndex((v) => v.id === minVariantId);
  return index !== undefined && index >= 0 ? index : fallbackIndex;
};

export const buildWishlistLineItem = (product: Product, variant: Variant): any => ({
  lineItemId: product.productId ?? '',
  productId: product.productId,
  productKey: product.productKey,
  productRef: product.productRef,
  name: product.name,
  count: 1,
  variant,
  addedAt: new Date(),
  _url: product._url,
});

export const buildProductUrl = (
  product: Product,
  sku?: string,
  isSearchResult = false,
  isToggleP2r = false,
): string => {
  const productSlug = product._url?.split('/')[1] || product.name?.trim().toLowerCase().replace(/\s+/g, '-') || '';

  const queryParams = new URLSearchParams();

  if (sku) {
    queryParams.set('sku', sku);
  }

  if (isSearchResult) {
    queryParams.set('sr', '1');
  }

  if (isToggleP2r) {
    queryParams.set('isToggleP2r', 'true');
  }

  return `/${productSlug}/p?${queryParams.toString()}`;
};

export const getUniqueTags = (tags?: string[]) =>
  Array.from(new Map((tags ?? []).map((tag) => [tag.toLowerCase(), tag])).values());
